import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiClock, FiSmartphone, FiCheckCircle, FiArrowRight, FiUser, FiCreditCard, FiTrendingUp } = FiIcons;

function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: FiClock,
      title: "Quick & Easy",
      description: "Complete your application in under 10 minutes"
    },
    {
      icon: FiShield,
      title: "Secure Process",
      description: "Bank-grade security to protect your information"
    },
    {
      icon: FiSmartphone,
      title: "Mobile Friendly",
      description: "Apply from anywhere using your phone or computer"
    },
    {
      icon: FiCheckCircle,
      title: "Instant Approval",
      description: "Get approval notification within 24 hours"
    }
  ];

  const accountTypes = [
    {
      icon: FiUser,
      title: "Savings Account",
      description: "Earn competitive interest on your savings",
      benefits: ["No minimum balance", "Free ATM withdrawals", "Mobile banking"]
    },
    {
      icon: FiCreditCard,
      title: "Current Account",
      description: "Perfect for everyday banking needs",
      benefits: ["Unlimited transactions", "Checkbook facility", "Overdraft facility"]
    },
    {
      icon: FiTrendingUp,
      title: "Investment Account",
      description: "Grow your wealth with our investment products",
      benefits: ["High returns", "Expert advice", "Flexible terms"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-agric-primary via-agric-secondary to-agric-accent">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750826998135-logo-dark.png" 
                alt="Agric Bank Ghana" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <a href="#" className="text-agric-primary hover:text-agric-secondary transition-colors">About</a>
              <a href="#" className="text-agric-primary hover:text-agric-secondary transition-colors">Services</a>
              <a href="#" className="text-agric-primary hover:text-agric-secondary transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              Open Your Account
              <span className="block text-agric-lime">In Minutes</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Join thousands of Ghanaians who trust Agric Bank for their banking needs. Start your journey to financial growth today.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={() => navigate('/apply')}
                className="bg-agric-lime hover:bg-agric-lime/90 text-agric-dark px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center gap-2"
              >
                Start Application
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-agric-primary px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300">
                Learn More
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-agric-primary mb-4">
              Why Choose Our Online Account Opening?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Experience the convenience of modern banking with our streamlined account opening process.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <div className="bg-agric-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-agric-primary" />
                </div>
                <h3 className="text-xl font-semibold text-agric-primary mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Account Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-agric-primary mb-4">
              Choose Your Account Type
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Select the account that best fits your financial goals and lifestyle.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {accountTypes.map((account, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="bg-agric-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={account.icon} className="w-8 h-8 text-agric-primary" />
                </div>
                <h3 className="text-2xl font-semibold text-agric-primary mb-3">{account.title}</h3>
                <p className="text-gray-600 mb-6">{account.description}</p>
                <ul className="space-y-2">
                  {account.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-agric-accent" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-agric-primary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join the Agric Bank family today and experience banking that grows with you.
            </p>
            <button
              onClick={() => navigate('/apply')}
              className="bg-agric-lime hover:bg-agric-lime/90 text-agric-dark px-10 py-4 rounded-lg font-semibold text-xl transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center gap-3"
            >
              Open Account Now
              <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-agric-dark text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750826998135-logo-dark.png" 
                alt="Agric Bank Ghana" 
                className="h-10 w-auto mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-300">
                Your trusted partner in agricultural and commercial banking in Ghana.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Branches</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <div className="space-y-2 text-gray-300">
                <p>📞 +233(0)302-210210</p>
                <p>✉️ customercare@agricbank.com</p>
                <p>📍 Accra, Ghana</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p>2025 © ADB.</p>
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-4">
                  <a href="#" className="hover:text-white transition-colors">Terms & Conditions</a>
                  <span>|</span>
                  <a href="#" className="hover:text-white transition-colors">Privacy Statement</a>
                </div>
                <div className="flex items-center gap-2">
                  <span>Powered by</span>
                  <a 
                    href="https://exceedbranding.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-agric-lime hover:text-agric-lime/80 transition-colors font-medium"
                  >
                    Exceed
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;